import { sanitizeDeep } from './sanitize';

export const parseViewBox = (viewBox) => {
  if (typeof viewBox !== 'string') {
    return { minX: 0, minY: 0, width: 100, height: 100 };
  }
  const parts = viewBox
    .trim()
    .split(/\s+/)
    .map((value) => Number(value));
  if (parts.length !== 4 || parts.some((value) => Number.isNaN(value))) {
    return { minX: 0, minY: 0, width: 100, height: 100 };
  }
  return {
    minX: parts[0],
    minY: parts[1],
    width: parts[2] || 100,
    height: parts[3] || 100,
  };
};

export const computeCoverScale = (frameW, frameH, naturalW, naturalH) => {
  const safeFrameW = Math.max(1, Number(frameW) || 1);
  const safeFrameH = Math.max(1, Number(frameH) || 1);
  const safeNaturalW = Math.max(1, Number(naturalW) || safeFrameW);
  const safeNaturalH = Math.max(1, Number(naturalH) || safeFrameH);
  return Math.max(safeFrameW / safeNaturalW, safeFrameH / safeNaturalH);
};

export const getFrameImageMetrics = (frame, content) => {
  const frameW = Math.max(1, Number(frame?.w) || 1);
  const frameH = Math.max(1, Number(frame?.h) || 1);
  const naturalW = Math.max(1, Number(content?.naturalW) || frameW);
  const naturalH = Math.max(1, Number(content?.naturalH) || frameH);
  const coverScale = computeCoverScale(frameW, frameH, naturalW, naturalH);
  const scale = Math.max(coverScale, Number(content?.scale) || coverScale);
  const imgW = naturalW * scale;
  const imgH = naturalH * scale;
  const minOffsetX = Math.min(0, frameW - imgW);
  const maxOffsetX = imgW >= frameW ? 0 : (frameW - imgW) / 2;
  const minOffsetY = Math.min(0, frameH - imgH);
  const maxOffsetY = imgH >= frameH ? 0 : (frameH - imgH) / 2;
  return {
    frameW,
    frameH,
    naturalW,
    naturalH,
    coverScale,
    scale,
    imgW,
    imgH,
    minOffsetX,
    maxOffsetX,
    minOffsetY,
    maxOffsetY,
  };
};

export const centerFrameContent = (frame, content, scaleOverride) => {
  const metrics = getFrameImageMetrics(frame, {
    ...content,
    scale: scaleOverride ?? content?.scale,
  });
  const imgW = metrics.naturalW * metrics.scale;
  const imgH = metrics.naturalH * metrics.scale;
  return {
    offsetX: (metrics.frameW - imgW) / 2,
    offsetY: (metrics.frameH - imgH) / 2,
  };
};

export const clampFrameContent = (frame, content) => {
  if (!content || content.type !== 'image') return content;
  const metrics = getFrameImageMetrics(frame, content);
  const centered = centerFrameContent(frame, content, metrics.scale);
  const rawOffsetX = Number.isFinite(Number(content.offsetX))
    ? Number(content.offsetX)
    : centered.offsetX;
  const rawOffsetY = Number.isFinite(Number(content.offsetY))
    ? Number(content.offsetY)
    : centered.offsetY;
  const offsetX = Math.min(Math.max(rawOffsetX, metrics.minOffsetX), metrics.maxOffsetX);
  const offsetY = Math.min(Math.max(rawOffsetY, metrics.minOffsetY), metrics.maxOffsetY);
  return sanitizeDeep({
    ...content,
    scale: metrics.scale,
    offsetX,
    offsetY,
  });
};

export const createFrameContentFromAsset = (frame, asset) => {
  if (!asset?.downloadUrl) return null;
  const naturalW = Math.max(1, Number(asset.width) || frame?.w || 1200);
  const naturalH = Math.max(1, Number(asset.height) || frame?.h || 1200);
  const coverScale = computeCoverScale(frame?.w, frame?.h, naturalW, naturalH);
  const baseContent = {
    type: 'image',
    assetId: asset.id,
    src: asset.downloadUrl,
    naturalW,
    naturalH,
    scale: coverScale,
    offsetX: 0,
    offsetY: 0,
  };
  const centered = centerFrameContent(frame, baseContent, coverScale);
  return clampFrameContent(frame, {
    ...baseContent,
    offsetX: centered.offsetX,
    offsetY: centered.offsetY,
  });
};
